import type { TFunction } from 'i18next';
import { subject } from '@casl/ability';

import type { AppAbility } from '@shared/types/ability';

export interface AppNavItem {
  to: string;
  label: string;
  exact?: boolean;
}

type Translate = TFunction;
type SystemRole = 'SYSTEM_ADMIN' | 'DEVELOPER' | 'SYSTEM_CLIENT';

const PRIVILEGED_ROLES: readonly SystemRole[] = ['SYSTEM_CLIENT', 'SYSTEM_ADMIN', 'DEVELOPER'];

function pushUnique(items: AppNavItem[], item: AppNavItem): void {
  if (items.some((existing) => existing.to === item.to)) {
    return;
  }
  items.push(item);
}

export function canAccessAnalyticsMove(ability: AppAbility): boolean {
  return ability.can('manage', 'all') || ability.can('read', 'AnalyticsMove');
}

export function canAccessTranslations(ability: AppAbility): boolean {
  return ability.can('manage', 'all') || ability.can('read', 'Translations');
}

export function canAccessTranslationLanguages(ability: AppAbility): boolean {
  return ability.can('manage', 'all') || ability.can('read', 'TranslationLanguages');
}

export function canAccessAnalyticsGear(ability: AppAbility): boolean {
  return ability.can('manage', 'all') || ability.can('read', 'AnalyticsGear');
}

export function canAccessInstructorWorkspace(ability: AppAbility): boolean {
  return ability.can('read', 'InstructorWorkspace');
}

export function canAccessAnyAnalytics(ability: AppAbility): boolean {
  return canAccessAnalyticsMove(ability) || canAccessAnalyticsGear(ability);
}

export function getDefaultAnalyticsPath(ability: AppAbility): string | null {
  if (canAccessAnalyticsMove(ability)) {
    return '/$locale/analytics-move/overview';
  }

  if (canAccessAnalyticsGear(ability)) {
    return '/$locale/analytics-gear';
  }

  return null;
}

export function getSyncNavItems(ability: AppAbility, label: string): AppNavItem[] {
  const canAccessSync = ability.can('manage', 'all') || ability.can('read', 'Dashboard');

  if (!canAccessSync) {
    return [];
  }

  return [
    {
      to: '/$locale/sync',
      label,
    },
  ];
}

export function canAccessErpSync(ability: AppAbility, systemRole: string | null): boolean {
  return systemRole === 'SYSTEM_ADMIN' && ability.can('manage', 'all');
}

export function canAccessHelpdesk(ability: AppAbility): boolean {
  return ability.can('read', 'HelpdeskTask');
}

/** Navigation derives from the same read abilities as the resource routes. */
export function getHelpdeskKnowledgePath(ability: AppAbility) {
  if (ability.can('read', 'HelpdeskArticle')) return '/$locale/crm/helpdesk/articles' as const;
  if (ability.can('read', 'HelpdeskAiPrompt')) return '/$locale/crm/helpdesk/prompts' as const;
  if (ability.can('read', 'HelpdeskAiInstruction'))
    return '/$locale/crm/helpdesk/instructions' as const;
  return null;
}

export function canAccessCrmCustomerDirectory(ability: AppAbility): boolean {
  return ability.can('read', 'CrmCustomerDirectory');
}

export function canAccessCrmCustomerProfile(ability: AppAbility): boolean {
  return ability.can('read', 'CrmCustomerProfile');
}

export function canAccessCrmCustomerMembershipDirectory(ability: AppAbility): boolean {
  return ability.can(
    'read',
    subject('CrmCustomerMembership', { customerId: 'crm-directory-membership-projection' })
  );
}

export function canAccessCrmCustomerDocuments(ability: AppAbility): boolean {
  return ability.can('read', 'CrmCustomerDocuments');
}

export function canAccessCrmCustomerLearning(ability: AppAbility): boolean {
  return ability.can('read', 'CrmCustomerLearning');
}

export function canAccessCrmCustomerSync(ability: AppAbility): boolean {
  return ability.can('read', 'CrmCustomerSync');
}

/**
 * The profile-scoped Payments projection is an operator surface. This derives
 * only from the backend-issued cross-user Payment ability; it is not a role or
 * raw Access-option check and does not authorize the Payment requests.
 */
export function canAccessCrmUserProfilePayments(ability: AppAbility): boolean {
  return ability.can('filterByCustomer', 'Payment');
}

export function canAccessPersonalFlow(ability: AppAbility, accountId: string | null): boolean {
  if (accountId === null) return false;
  return (
    ability.can('manage', 'all') ||
    ability.can('read', subject('UserProfile', { accountId, id: undefined }))
  );
}

export function getErpSyncNavItems(
  ability: AppAbility,
  label: string,
  systemRole: string | null
): AppNavItem[] {
  if (!canAccessErpSync(ability, systemRole)) {
    return [];
  }

  return [
    {
      to: '/$locale/erp-sync',
      label,
    },
  ];
}

export function getAnalyticsNavItems(ability: AppAbility, t: Translate): AppNavItem[] {
  const items: AppNavItem[] = [];

  if (canAccessAnalyticsMove(ability)) {
    items.push({
      to: '/$locale/analytics-move/overview',
      label: t('navigation.analyticsMove', 'Analytics Move'),
    });
  }

  if (canAccessAnalyticsGear(ability)) {
    items.push({
      to: '/$locale/analytics-gear',
      label: t('navigation.analyticsGear', 'Analytics Gear'),
    });
  }

  return items;
}

export function getUiLibraryNavItems(ability: AppAbility, label: string): AppNavItem[] {
  const canAccessUiLibrary = ability.can('manage', 'all') || ability.can('read', 'UiLibrary');

  if (!canAccessUiLibrary) {
    return [];
  }

  return [
    {
      to: '/$locale/ui-library',
      label,
    },
  ];
}

export function getAppNavItems(
  ability: AppAbility,
  t: Translate,
  systemRole: SystemRole | null = null,
  _businessRoles: readonly string[] = [],
  accountId: string | null = null
): AppNavItem[] {
  // Retain the positional API for callers; business-role names are not authority.
  void _businessRoles;
  const items: AppNavItem[] = [];
  if (systemRole === null) {
    return items;
  }
  const canAccessDashboard = ability.can('manage', 'all') || ability.can('read', 'Dashboard');

  if (canAccessPersonalFlow(ability, accountId)) {
    pushUnique(items, {
      to: '/$locale/student/personal-flow',
      label: t('navigation.personalFlow', 'Personal Flow'),
    });
  }
  pushUnique(items, {
    to: '/$locale/courses',
    label: t('navigation.courses', 'Courses'),
  });

  if (canAccessInstructorWorkspace(ability)) {
    pushUnique(items, {
      to: '/$locale/instructor',
      label: t('navigation.forInstructors', 'For Instructors'),
    });
  }

  if (canAccessCrmCustomerDirectory(ability)) {
    pushUnique(items, {
      to: '/$locale/crm',
      label: t('navigation.crm', 'CRM'),
    });
  }

  const hasPrivilegedRole = PRIVILEGED_ROLES.includes(systemRole);

  pushUnique(items, {
    to: '/$locale/payments',
    label: t('navigation.paymentsCredit', 'Payments/Credit'),
  });

  if (canAccessDashboard && hasPrivilegedRole) {
    pushUnique(items, {
      to: '/$locale/team-dashboard',
      label: t('navigation.teamDashboard', 'Team Dashboard'),
    });
  }

  for (const item of getSyncNavItems(ability, t('navigation.sync', 'Sync'))) {
    pushUnique(items, item);
  }
  for (const item of getErpSyncNavItems(ability, t('navigation.erpSync', 'ERP Sync'), systemRole)) {
    pushUnique(items, item);
  }
  for (const item of getAnalyticsNavItems(ability, t)) {
    pushUnique(items, item);
  }

  if (canAccessAnalyticsGear(ability)) {
    pushUnique(items, {
      to: '/$locale/shopify/stock-control',
      label: t('navigation.shopifyManagement', 'Shopify Management'),
    });
    pushUnique(items, {
      to: '/$locale/gear-erp/products',
      label: t('productCatalog.title', 'Product Catalog'),
    });
    pushUnique(items, {
      to: '/$locale/warehouse',
      label: t('warehouse.title', 'Warehouse'),
    });
  }

  if (canAccessTranslations(ability)) {
    pushUnique(items, {
      to: '/$locale/translations',
      label: t('navigation.translations', 'Translations'),
    });
  }

  const helpdeskPath = canAccessHelpdesk(ability)
    ? '/$locale/crm/helpdesk'
    : getHelpdeskKnowledgePath(ability);
  if (helpdeskPath && !canAccessCrmCustomerDirectory(ability)) {
    pushUnique(items, { to: helpdeskPath, label: t('helpdesk.title', 'Helpdesk') });
  }

  if (ability.can('manage', 'all') || ability.can('read', 'AdminTools')) {
    pushUnique(items, { to: '/$locale/admin', label: t('roles.admin', 'Admin') });
  }

  for (const item of getUiLibraryNavItems(ability, t('uiLibrary.title', 'UI Library'))) {
    pushUnique(items, item);
  }

  return items;
}
